/**
 * pp-home-hero-ai — client wiring del hero AI del home.
 *
 * Si el assistant Paco (<pp-assistant>) está montado en la página, tanto
 * el submit del form como el click en los chips abren el chat con el
 * texto como primer mensaje del cliente. Si no está, todo cae a su
 * navegación nativa (form → /search, chip → href configurado en admin).
 *
 * Enhancements:
 * - Trim del input value en submit.
 * - Submit vacío hace focus en vez de navegar.
 * - Typewriter placeholder rotativo.
 * - Chip handoff vía event delegation (sobrevive a section reloads).
 */
(function () {
  'use strict';

  /**
   * El server manda solo 2 copias del set de tiles por row (markup ~2/3
   * más ligero) y un script inline en el section las triplica a 6 antes
   * del primer paint. Este respaldo cubre el theme editor: en un
   * section reload de Shopify los <script> inline del HTML inyectado NO
   * se re-ejecutan, así que duplicamos aquí. Idempotente vía data-pp-dup.
   */
  function duplicateMarqueeRows(scope) {
    var rows = (scope || document).querySelectorAll('.pp-hero-ai__row');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-pp-dup')) continue;
      rows[i].setAttribute('data-pp-dup', '1');
      var h = rows[i].innerHTML;
      rows[i].insertAdjacentHTML('beforeend', h + h);
    }
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('.pp-hero-ai__row')) {
      duplicateMarqueeRows(e.target);
    }
  });

  function init() {
    var form = document.querySelector('[data-pp-hero-ai-form]');
    if (!form) return;
    duplicateMarqueeRows(document);
    var input = form.querySelector('input[name="q"]');
    if (!input) return;

    form.addEventListener('submit', function (e) {
      var raw = (input.value || '').trim();
      if (!raw) {
        e.preventDefault();
        input.focus();
        return;
      }
      // Normaliza dimensiones / SKU para mejorar el matching en /search
      var normalized = normalizeQuery(raw);
      input.value = normalized || raw;

      // En mobile el hero ES un buscador real → submit nativo a /search.
      // En desktop seguimos con el chat-handoff (overlay + assistant).
      var isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (!isMobile && document.querySelector('pp-assistant')) {
        e.preventDefault();
        document.dispatchEvent(
          new CustomEvent('pp-assistant:open', { detail: { message: raw } })
        );
        input.value = '';
      }
    });

    initChipHandoff();
    initAssistantCta();
    initTypewriterPlaceholder(input);
    initPredictiveSearch(input);
  }

  /**
   * Predictive search en el hero mobile: cuando el user escribe, fetch
   * /search/suggest.json y muestra hasta 6 productos como sugerencias
   * con foto + título + precio. Click navega al producto. Esc cierra.
   */
  function initPredictiveSearch(input) {
    var dropdown = document.querySelector('[data-pp-hero-suggest]');
    if (!dropdown) return;
    var debounceTimer = null;
    var lastQuery = '';
    var currentReq = 0;

    function clearDropdown() {
      while (dropdown.firstChild) dropdown.removeChild(dropdown.firstChild);
    }
    function hide() {
      dropdown.setAttribute('hidden', '');
      clearDropdown();
    }
    function show() {
      dropdown.removeAttribute('hidden');
    }
    function makeEl(tag, cls, text) {
      var el = document.createElement(tag);
      if (cls) el.className = cls;
      if (text != null) el.textContent = text;
      return el;
    }

    function renderEmpty(query) {
      clearDropdown();
      var box = makeEl('div', 'pp-hero-ai__suggest-empty');
      box.appendChild(document.createTextNode('Sin sugerencias para "'));
      box.appendChild(makeEl('strong', null, query));
      box.appendChild(document.createTextNode('". Toca '));
      box.appendChild(makeEl('strong', null, 'Pregúntale a la IA'));
      box.appendChild(document.createTextNode(' para que Paco te ayude.'));
      dropdown.appendChild(box);
      show();
    }

    function renderProducts(products) {
      clearDropdown();
      products.forEach(function (p) {
        var item = makeEl('a', 'pp-hero-ai__suggest-item');
        item.setAttribute('href', p.url || '#');
        item.setAttribute('role', 'option');
        var imgUrl = (p.featured_image && p.featured_image.url) || p.image || '';
        if (imgUrl) {
          var img = document.createElement('img');
          img.className = 'pp-hero-ai__suggest-img';
          img.src = sizeImg(imgUrl, 120);
          img.alt = '';
          img.loading = 'lazy';
          item.appendChild(img);
        } else {
          item.appendChild(makeEl('div', 'pp-hero-ai__suggest-img'));
        }
        var body = makeEl('div', 'pp-hero-ai__suggest-body');
        body.appendChild(makeEl('div', 'pp-hero-ai__suggest-title', p.title || ''));
        var priceTxt = formatPrice(p.price);
        if (priceTxt) {
          var unit = inferUnit(p.title);
          var label = unit ? (priceTxt + ' c/' + unit) : priceTxt;
          body.appendChild(makeEl('div', 'pp-hero-ai__suggest-price', label));
        }
        item.appendChild(body);
        dropdown.appendChild(item);
      });
      show();
    }

    function fetchSuggestions(query) {
      currentReq++;
      var thisReq = currentReq;
      // Normaliza la query antes de mandarla (igual que header-search):
      // "15x15" no matchea porque Shopify tokeniza por separadores;
      // "15 x 15" sí. Sin esto el user que escribe sin espacios pega
      // contra "Sin sugerencias" aunque haya producto literal.
      var normalized = normalizeQuery(query) || query;
      var url = '/search/suggest.json'
        + '?q=' + encodeURIComponent(normalized)
        + '&resources[type]=product'
        + '&resources[limit]=6'
        + '&resources[options][unavailable_products]=last'
        // resources[options][fields] le dice a Shopify qué campos buscar. Por
        // default NO incluye variants.sku → los SKU (ej "K-5411") no salían en
        // este buscador del home, aunque sí en header-search y el sheet móvil
        // (que ya los pedían). Se alinea a los mismos fields.
        + '&resources[options][fields]=title,product_type,variants.title,variants.sku,tag,vendor';
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (thisReq !== currentReq) return; // stale request
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          if (products.length === 0) renderEmpty(query);
          else renderProducts(products);
        })
        .catch(function () { /* silent */ });
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (q === lastQuery) return;
      lastQuery = q;
      clearTimeout(debounceTimer);
      if (q.length < 2) {
        hide();
        return;
      }
      debounceTimer = setTimeout(function () { fetchSuggestions(q); }, 180);
    });

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2 && dropdown.firstChild) show();
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        hide();
        input.blur();
      }
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-pp-hero-suggest]')) return;
      if (e.target === input) return;
      hide();
    });
  }

  function sizeImg(url, size) {
    if (!url) return '';
    return url.replace(/(\.(?:jpe?g|png|webp|gif))(\?.*)?$/i, '_' + size + 'x$1$2');
  }
  function formatPrice(p) {
    if (typeof p === 'string' && p.indexOf('$') >= 0) return p;
    var n = typeof p === 'number' ? p / 100 : parseFloat(p) / 100;
    if (!isFinite(n)) return '';
    return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' MXN';
  }

  /**
   * Normaliza la query del usuario para que matchee títulos
   * independiente del formato. Port del header-search:
   *   · "15x15x15" / "15 X 15 X 15" / "15×15×15" → "15 x 15 x 15"
   *   · "15cm x 15cm" → "15 x 15"
   *   · "p1111" / "P-1111" → "P-1111" (SKU canonical)
   */
  function normalizeQuery(raw) {
    var q = (raw || '').trim();
    if (!q) return '';
    var skuMatch = q.match(/^([A-Za-z]{1,5})[\s\-_]?(\d{3,})([A-Za-z]?)$/);
    if (skuMatch) {
      return skuMatch[1].toUpperCase() + '-' + skuMatch[2] + skuMatch[3].toUpperCase();
    }
    // SKU completo con guion ("CPK-190-48X50"): va TAL CUAL — misma regla
    // y mismo porqué que en pp-header-search.js (la regla de medidas lo
    // partía en "48 x 50" y Shopify ya no lo encontraba).
    if (/^[A-Za-z]{1,6}-[A-Za-z0-9-]+$/.test(q)) {
      return q.toUpperCase();
    }
    q = q.replace(/(\d)\s*(cm|mm|in|inch|pulgadas?|pulg)\b\.?/gi, '$1');
    var prev;
    do {
      prev = q;
      q = q.replace(/(\d)\s*[xX×*]\s*(\d)/g, '$1 x $2');
    } while (q !== prev);
    q = q.replace(/\s+/g, ' ').trim();
    return q;
  }

  /**
   * Infiere la unidad ("caja", "sobre", "bolsa", "rollo", etc.) a partir
   * del título del producto. Usado para mostrar "$X c/unidad" al lado
   * del precio en la sugerencia.
   */
  function inferUnit(title) {
    var t = (title || '').toLowerCase();
    if (/\bcaja(s)?\b/.test(t)) return 'caja';
    if (/\bsobre(s)?\b/.test(t)) return 'sobre';
    if (/\bbolsa(s)?\b/.test(t)) return 'bolsa';
    if (/\brollo(s)?\b/.test(t)) return 'rollo';
    if (/\betiqueta(s)?\b/.test(t)) return 'etiqueta';
    if (/\besquinero(s)?\b/.test(t)) return 'esquinero';
    if (/\bcinta(s)?\b/.test(t)) return 'cinta';
    if (/\bfleje(s)?\b/.test(t)) return 'fleje';
    if (/\bemplaye\b/.test(t)) return 'rollo';
    return '';
  }

  /**
   * CTA mobile-only "¿No sabes qué buscar? Pregúntale a la IA".
   * Dispara el evento que abre el chat de Paco. Si Paco no está montado,
   * el botón degrada a no-op (no rompe nada).
   */
  function initAssistantCta() {
    var cta = document.querySelector('[data-pp-hero-assistant]');
    if (!cta) return;
    cta.addEventListener('click', function (e) {
      e.preventDefault();
      if (!document.querySelector('pp-assistant')) return;
      document.dispatchEvent(
        new CustomEvent('pp-assistant:open', { detail: {} })
      );
    });
  }

  /**
   * Chips de ejemplo del hero — mismo contrato que el form: si el
   * assistant Paco está montado en la página, click abre el chat con
   * el texto del chip como primer mensaje (data-pp-chip-message). Si
   * no está, dejamos que el <a href> haga su navegación nativa (URL
   * configurada por admin como fallback).
   *
   * Event delegation desde el contenedor para sobrevivir a re-renders
   * del section del customizer de Shopify.
   */
  function initChipHandoff() {
    var container = document.querySelector('.pp-hero-ai__chat-chips');
    if (!container) return;

    container.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-pp-chip-message]');
      if (!chip || !container.contains(chip)) return;

      if (!document.querySelector('pp-assistant')) return;

      var message = (chip.getAttribute('data-pp-chip-message') || '').trim();
      if (!message) return;

      e.preventDefault();
      document.dispatchEvent(
        new CustomEvent('pp-assistant:open', { detail: { message: message } })
      );
    });
  }

  /**
   * Typewriter placeholder — mismo patrón que pp-header-search.js.
   * Lee data-pp-hero-phrases (JSON array de strings) y va escribiendo
   * carácter por carácter, pausa al completar, borra, pasa a la
   * siguiente. Si el usuario escribió algo, pausa el ciclo.
   *
   * Prefix "Ej: " sigue el patrón del header search ("Estoy buscando")
   * para coherencia tipográfica entre los dos inputs principales.
   */
  function initTypewriterPlaceholder(input) {
    var phrases = [];
    try {
      phrases = JSON.parse(input.getAttribute('data-pp-hero-phrases') || '[]');
    } catch (e) { return; }
    if (!phrases.length) return;

    var PREFIX = 'Busco ';
    var phraseIdx = 0;
    var charIdx = 0;
    var deleting = false;
    var timer;

    function tick() {
      clearTimeout(timer);
      if (input.value && input.value.length > 0) {
        // User está escribiendo, pausamos y revisamos en 800ms
        timer = setTimeout(tick, 800);
        return;
      }
      var phrase = phrases[phraseIdx];
      if (!deleting) {
        charIdx++;
        input.setAttribute('placeholder', PREFIX + phrase.slice(0, charIdx));
        if (charIdx >= phrase.length) {
          deleting = true;
          timer = setTimeout(tick, 1600);
          return;
        }
        timer = setTimeout(tick, 60 + Math.random() * 50);
      } else {
        charIdx--;
        input.setAttribute('placeholder', PREFIX + phrase.slice(0, charIdx));
        if (charIdx <= 0) {
          deleting = false;
          phraseIdx = (phraseIdx + 1) % phrases.length;
          timer = setTimeout(tick, 220);
          return;
        }
        timer = setTimeout(tick, 30);
      }
    }
    tick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
